import { Sheet, SheetContent, SheetClose } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { X, Plus, Minus } from 'lucide-react';
import { useState } from 'react';
import { getAnySalePrice, getDisplayPrice } from '@/utils/salePricing';

const BUBBLES_RESTAURANT_IDS = [
  '2874e157-906f-4254-ba53-b96d30034b03',
  'b753580d-e089-4238-a745-0abee68e4cf1',
  '2dbd04da-eb41-460f-842d-700eb034d719'
];

interface MenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  is_available: boolean;
  original_price?: number;
}

interface ProductDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem | null;
  currentQuantity: number;
  onAddToCart: () => void;
  onUpdateQuantity: (newQuantity: number) => void;
  restaurantId?: string;
  deliveryType?: 'delivery' | 'pickup';
  vendorName?: string;
  categoryName?: string;
}

export const ProductDetailsModal = ({
  isOpen,
  onClose,
  item,
  currentQuantity,
  onAddToCart,
  onUpdateQuantity,
  restaurantId,
  deliveryType = 'delivery',
  vendorName = '',
  categoryName = '',
}: ProductDetailsModalProps) => {
  const [quantity, setQuantity] = useState(1);

  if (!item) return null;

  const handleAddToCart = () => {
    if (currentQuantity === 0) {
      onAddToCart();
    } else {
      onUpdateQuantity(currentQuantity + quantity);
    }
    onClose();
  };

  const decreaseQuantity = () => {
    if (quantity > 1) setQuantity(quantity - 1);
  };

  const increaseQuantity = () => {
    setQuantity(quantity + 1);
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="p-0 h-auto max-h-[85vh] rounded-t-3xl">
        <SheetClose className="absolute right-4 top-4 z-10 rounded-full bg-background/80 p-2 hover:bg-background">
          <X className="h-5 w-5" />
        </SheetClose>

        <div className="flex flex-col overflow-y-auto max-h-[85vh]">
          {/* Product Image */}
          <div className="w-full max-h-[50vh] bg-muted relative flex items-center justify-center">
            {item.image_url ? (
              <img
                src={item.image_url}
                alt={item.name}
                className="w-full h-full object-contain max-h-[50vh]"
              />
            ) : (
              <div className="w-full h-64 flex items-center justify-center text-muted-foreground text-6xl">
                📦
              </div>
            )}
          </div>

          {/* Product Details */}
          <div className="p-6 space-y-4">
            <h2 className="text-2xl font-bold text-foreground">{item.name}</h2>
            
            {item.description && (
              <p className="text-muted-foreground leading-relaxed">
                {item.description}
              </p>
            )}

            <div className="flex flex-col">
              {/* Bubbles discount display */}
              {item.original_price && restaurantId && BUBBLES_RESTAURANT_IDS.includes(restaurantId) && deliveryType === 'delivery' && (
                <div className="text-lg text-muted-foreground line-through">
                  D{item.original_price.toFixed(0)}
                </div>
              )}
              {/* Sale price display (Loris Perfum & Phone City) */}
              {(() => {
                const sale = getAnySalePrice(vendorName, item.name, categoryName, item.price);
                if (sale) {
                  return (
                    <>
                      <div className="text-lg text-muted-foreground line-through decoration-destructive">
                        D{sale.originalPrice.toFixed(0)}
                      </div>
                      <div className="text-3xl font-bold text-foreground">
                        D{sale.salePrice.toFixed(0)}
                      </div>
                    </>
                  );
                }
                // Default price display (including Bubbles pickup logic)
                return (
                  <div className="text-3xl font-bold text-foreground">
                    D{(restaurantId && BUBBLES_RESTAURANT_IDS.includes(restaurantId) && deliveryType === 'pickup' && item.original_price ? item.original_price : item.price).toFixed(0)}
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Bottom Actions */}
          <div className="px-6 pb-6 flex items-center gap-3">
            {/* Quantity Controls */}
            <div className="flex items-center gap-3 border border-border rounded-lg px-4 py-3">
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 rounded-full hover:bg-muted"
                onClick={decreaseQuantity}
                disabled={quantity <= 1}
              >
                <Minus className="h-4 w-4" />
              </Button>
              <span className="font-semibold text-lg min-w-[2rem] text-center">
                {quantity}
              </span>
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8 rounded-full hover:bg-muted"
                onClick={increaseQuantity}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>

            {/* Add to Cart Button */}
            <Button
              className="flex-1 bg-destructive hover:bg-destructive/90 text-white h-12 text-base font-semibold"
              onClick={handleAddToCart}
              disabled={!item.is_available}
            >
              {!item.is_available ? 'Out of Stock' : 'Add to cart'}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
